import React, { useEffect, useState, useCallback, useMemo } from "react";
import {
  Paper, Grid, Card, CardContent, Typography, TextField, MenuItem
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, Legend
} from "recharts";
import { toast } from "react-toastify";
import MainContainer from "../../components/MainContainer";
import PageHeader from "../../components/PageHeader";
import DataTable from "../../components/DataTable";
import StatusChip from "../../components/StatusChip";
import api from "../../services/api";

const useStyles = makeStyles((theme) => ({
  body: { paddingTop: theme.spacing(2) },
  kpi: { textAlign: "center" },
  kpiVal: { fontSize: 28, fontWeight: 700, lineHeight: 1.1 },
  kpiLbl: { fontSize: 11, color: theme.palette.text.secondary, textTransform: "uppercase" },
  chartBox: { height: 280, marginTop: 8 },
  section: { padding: theme.spacing(2), marginTop: theme.spacing(2) },
  tableWrap: { marginTop: theme.spacing(1.5) },
  num: { fontVariantNumeric: "tabular-nums" },
  toneSuccess: { color: theme.palette.success.main },
  toneDanger: { color: theme.palette.error.main },
  toneInfo: { color: theme.palette.info.main },
}));

const STATUS_LABEL = {
  open: "Aberta",
  in_progress: "Em andamento",
  review: "Revisão",
  blocked: "Bloqueada",
  done: "Concluída",
  cancelled: "Cancelada"
};
const STATUS_COLOR = {
  open: "#9e9e9e",
  in_progress: "#2196F3",
  review: "#9C27B0",
  blocked: "#F44336",
  done: "#4CAF50",
  cancelled: "#607D8B"
};
const PRIORITY_LABEL = { low: "Baixa", medium: "Média", high: "Alta", urgent: "Urgente" };

const TONE_CLASS = (classes, tone) =>
  tone === "success" ? classes.toneSuccess
  : tone === "danger" ? classes.toneDanger
  : tone === "info" ? classes.toneInfo
  : "";

const Productivity = () => {
  const classes = useStyles();
  const [tasks, setTasks] = useState([]);
  const [projects, setProjects] = useState([]);
  const [users, setUsers] = useState([]);
  const [filterProject, setFilterProject] = useState("");
  const [filterUser, setFilterUser] = useState("");
  const [dueAfter, setDueAfter] = useState("");
  const [dueBefore, setDueBefore] = useState("");

  const fetchAll = useCallback(async () => {
    try {
      const params = {};
      if (filterProject) params.projectId = filterProject;
      if (filterUser) params.assigneeId = filterUser;
      if (dueAfter) params.dueAfter = dueAfter;
      if (dueBefore) params.dueBefore = dueBefore;
      const [t, p, u] = await Promise.all([
        api.get("/tasks", { params }),
        api.get("/projects"),
        api.get("/users")
      ]);
      setTasks(Array.isArray(t.data) ? t.data : (t.data?.tasks || []));
      setProjects(Array.isArray(p.data) ? p.data : (p.data?.projects || p.data?.records || []));
      setUsers(Array.isArray(u.data) ? u.data : (u.data?.users || []));
    } catch (e) {
      toast.error("Falha ao carregar dados de produtividade");
    }
  }, [filterProject, filterUser, dueAfter, dueBefore]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const stats = useMemo(() => {
    const now = new Date();
    const total = tasks.length;
    let open = 0, done = 0, overdue = 0, inProgress = 0, blocked = 0;
    const byStatus = {};
    const byPriority = {};
    const byUser = {};
    const byProject = {};
    let estimateSum = 0;
    let actualSum = 0;

    tasks.forEach((t) => {
      const st = t.status || "open";
      byStatus[st] = (byStatus[st] || 0) + 1;
      const pr = t.priority || "medium";
      byPriority[pr] = (byPriority[pr] || 0) + 1;
      // Cancelada nao conta como aberta nem como concluida.
      if (st === "done") done++;
      else if (st !== "cancelled") open++;
      if (st === "in_progress") inProgress++;
      if (st === "blocked") blocked++;
      if (t.dueDate && st !== "done" && st !== "cancelled" && new Date(t.dueDate) < now) overdue++;

      const uname = t.assignee?.name || "Sem responsável";
      const uid = t.assigneeId || 0;
      const ukey = `${uid}|${uname}`;
      if (!byUser[ukey]) byUser[ukey] = { id: uid, name: uname, total: 0, done: 0, open: 0, overdue: 0 };
      byUser[ukey].total++;
      if (st === "done") byUser[ukey].done++;
      else if (st !== "cancelled") byUser[ukey].open++;
      if (t.dueDate && st !== "done" && st !== "cancelled" && new Date(t.dueDate) < now) byUser[ukey].overdue++;

      const pid = t.projectId || 0;
      const pname = projects.find(x => x.id === pid)?.name || (pid ? `#${pid}` : "Avulsas");
      const pkey = `${pid}|${pname}`;
      if (!byProject[pkey]) byProject[pkey] = { id: pid, name: pname, total: 0, done: 0, open: 0, overdue: 0 };
      byProject[pkey].total++;
      if (st === "done") byProject[pkey].done++;
      else if (st !== "cancelled") byProject[pkey].open++;
      if (t.dueDate && st !== "done" && st !== "cancelled" && new Date(t.dueDate) < now) byProject[pkey].overdue++;

      if (t.estimateHours) estimateSum += Number(t.estimateHours);
      if (t.actualHours) actualSum += Number(t.actualHours);
    });

    const completion = total > 0 ? Math.round((done / total) * 100) : 0;
    const statusData = Object.keys(byStatus).map(k => ({
      name: STATUS_LABEL[k] || k, value: byStatus[k], key: k
    }));
    const priorityData = Object.keys(byPriority).map(k => ({
      name: PRIORITY_LABEL[k] || k, value: byPriority[k]
    }));
    const userData = Object.values(byUser)
      .sort((a, b) => b.total - a.total)
      .slice(0, 10);
    const projectData = Object.values(byProject)
      .sort((a, b) => b.total - a.total)
      .slice(0, 10);

    return {
      total, open, done, overdue, inProgress, blocked, completion,
      estimateSum, actualSum,
      statusData, priorityData, userData, projectData
    };
  }, [tasks, projects]);

  // "Top responsáveis" e "Por projeto" mostram exatamente as mesmas seis
  // colunas — só muda o rótulo da primeira. Uma definição, dois usos.
  const breakdownColumns = (firstLabel) => [
    { key: "name", label: firstLabel, render: r => <Typography variant="subtitle2">{r.name}</Typography> },
    { key: "total", label: "Total", align: "right", render: r => <span className={classes.num}>{r.total}</span> },
    { key: "open", label: "Abertas", align: "right", render: r => <span className={classes.num}>{r.open}</span> },
    { key: "done", label: "Concluídas", align: "right", render: r => <span className={classes.num}>{r.done}</span> },
    {
      key: "overdue",
      label: "Atrasadas",
      align: "right",
      render: r => (r.overdue > 0
        ? <StatusChip tone="danger" label={String(r.overdue)} />
        : <span className={classes.num}>0</span>),
    },
    {
      key: "completion",
      label: "Conclusão",
      align: "right",
      render: r => (
        <span className={classes.num}>
          {r.total > 0 ? `${Math.round((r.done / r.total) * 100)}%` : "—"}
        </span>
      ),
    },
  ];

  return (
    <MainContainer>
      <PageHeader
        title="Produtividade"
        subtitle="Distribuição das tarefas por status, prioridade, responsável e projeto."
        filters={
          <>
            <TextField
              select label="Projeto"
              value={filterProject} onChange={(e) => setFilterProject(e.target.value)}
              style={{ minWidth: 200 }}
            >
              <MenuItem value="">Todos</MenuItem>
              {projects.map(p => <MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>)}
            </TextField>
            <TextField
              select label="Responsável"
              value={filterUser} onChange={(e) => setFilterUser(e.target.value)}
              style={{ minWidth: 200 }}
            >
              <MenuItem value="">Todos</MenuItem>
              {users.map(u => <MenuItem key={u.id} value={u.id}>{u.name}</MenuItem>)}
            </TextField>
            <TextField
              label="Prazo de" type="date" InputLabelProps={{ shrink: true }}
              value={dueAfter} onChange={(e) => setDueAfter(e.target.value)}
            />
            <TextField
              label="Prazo até" type="date" InputLabelProps={{ shrink: true }}
              value={dueBefore} onChange={(e) => setDueBefore(e.target.value)}
            />
          </>
        }
      />

      <div className={classes.body}>
        <Grid container spacing={2}>
          {[
            { lbl: "Total", val: stats.total },
            { lbl: "Abertas", val: stats.open, tone: "info" },
            { lbl: "Em andamento", val: stats.inProgress, tone: "info" },
            { lbl: "Bloqueadas", val: stats.blocked, tone: "danger" },
            { lbl: "Concluídas", val: stats.done, tone: "success" },
            { lbl: "Atrasadas", val: stats.overdue, tone: "danger" },
            { lbl: "Conclusão", val: `${stats.completion}%` }
          ].map((k) => (
            <Grid key={k.lbl} item xs={6} sm={4} md={2}>
              <Card>
                <CardContent className={classes.kpi}>
                  <div className={`${classes.kpiVal} ${TONE_CLASS(classes, k.tone)}`}>{k.val}</div>
                  <div className={classes.kpiLbl}>{k.lbl}</div>
                </CardContent>
              </Card>
            </Grid>
          ))}
          <Grid item xs={6} sm={4} md={2}>
            <Card>
              <CardContent className={classes.kpi}>
                <div className={classes.kpiVal}>
                  {stats.actualSum.toFixed(1)}/{stats.estimateSum.toFixed(1)}h
                </div>
                <div className={classes.kpiLbl}>Real / Estimado</div>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        <Grid container spacing={2} style={{ marginTop: 8 }}>
          <Grid item xs={12} md={6}>
            <Paper className={classes.section}>
              <Typography variant="subtitle1" style={{ fontWeight: 600 }}>Por status</Typography>
              <div className={classes.chartBox}>
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={stats.statusData}
                      dataKey="value"
                      nameKey="name"
                      outerRadius={90}
                      label
                    >
                      {stats.statusData.map((d) => (
                        <Cell key={d.key} fill={STATUS_COLOR[d.key] || "#888"} />
                      ))}
                    </Pie>
                    <Tooltip />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </Paper>
          </Grid>
          <Grid item xs={12} md={6}>
            <Paper className={classes.section}>
              <Typography variant="subtitle1" style={{ fontWeight: 600 }}>Por prioridade</Typography>
              <div className={classes.chartBox}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={stats.priorityData}>
                    <XAxis dataKey="name" />
                    <YAxis allowDecimals={false} />
                    <Tooltip />
                    <Bar dataKey="value" fill="#3F51B5" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </Paper>
          </Grid>
        </Grid>

        <Paper className={classes.section}>
          <Typography variant="subtitle1" style={{ fontWeight: 600 }}>Top responsáveis</Typography>
          <div className={classes.chartBox}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={stats.userData}>
                <XAxis dataKey="name" hide />
                <YAxis allowDecimals={false} />
                <Tooltip />
                <Legend />
                <Bar dataKey="open" name="Abertas" stackId="a" fill="#2196F3" />
                <Bar dataKey="done" name="Concluídas" stackId="a" fill="#4CAF50" />
                <Bar dataKey="overdue" name="Atrasadas" fill="#F44336" />
              </BarChart>
            </ResponsiveContainer>
          </div>
          <div className={classes.tableWrap}>
            <DataTable columns={breakdownColumns("Responsável")} rows={stats.userData}
              getRowKey={(u) => `${u.id}-${u.name}`}
              emptyTitle="Sem dados no filtro atual"
              emptyDescription="Nenhuma tarefa corresponde aos filtros escolhidos." />
          </div>
        </Paper>

        <Paper className={classes.section}>
          <Typography variant="subtitle1" style={{ fontWeight: 600 }}>Por projeto</Typography>
          <div className={classes.chartBox}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={stats.projectData}>
                <XAxis dataKey="name" hide />
                <YAxis allowDecimals={false} />
                <Tooltip />
                <Legend />
                <Bar dataKey="open" name="Abertas" stackId="a" fill="#2196F3" />
                <Bar dataKey="done" name="Concluídas" stackId="a" fill="#4CAF50" />
                <Bar dataKey="overdue" name="Atrasadas" fill="#F44336" />
              </BarChart>
            </ResponsiveContainer>
          </div>
          <div className={classes.tableWrap}>
            <DataTable columns={breakdownColumns("Projeto")} rows={stats.projectData}
              getRowKey={(p) => `${p.id}-${p.name}`}
              emptyTitle="Sem dados no filtro atual"
              emptyDescription="Nenhuma tarefa corresponde aos filtros escolhidos." />
          </div>
        </Paper>
      </div>
    </MainContainer>
  );
};

export default Productivity;
